import { Button } from '@heroui/react'
import {
  type BulkInviteRequest,
  type BulkInviteRowStatus,
  type BulkInviteStatus,
  isBulkInviteActive,
  useBulkInviteRequestDetails,
  useBulkInviteRequests,
} from '@qse/api'
import { EmptyState, EmptyStateIcon, TableComponent } from '@qse/ui'
import { ArrowLeft, Upload } from 'lucide-react'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'

import CountStat from './count-stat'
import EmailsNotSentNote from './emails-not-sent-note'
import { formatInitiator, getBulkInviteProgress, getBulkInviteResolvedRows } from './helpers'
import RowDetailsTable from './row-details-table'
import StatusChip from './status-chip'
import StatusFilterChips from './status-filter-chips'

interface HistoryColumn {
  name: string
  uid: string
}

const columns: HistoryColumn[] = [
  { name: 'File', uid: 'file' },
  { name: 'Initiated by', uid: 'initiator' },
  { name: 'Status', uid: 'status' },
  { name: 'Progress', uid: 'progress' },
  { name: 'Created', uid: 'created' },
  { name: '', uid: 'actions' },
]

const STATUS_OPTIONS: { key: BulkInviteStatus | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'QUEUED', label: 'Queued' },
  { key: 'PROCESSING', label: 'Processing' },
  { key: 'SUCCEEDED', label: 'Succeeded' },
  { key: 'PARTIAL', label: 'Partial' },
  { key: 'FAILED', label: 'Failed' },
]

const DETAILS_PAGE_SIZE = 50

type DetailsFilterKey = 'all' | 'invited' | 'already' | 'failed'

/**
 * Chip → row outcomes, grouped the way an admin thinks about them: "Invited"
 * is every row an invitation was actually created for (whatever happened to it
 * after), "Already invited" is the pre-dispatch skips for known emails, and
 * "Failed" is rows we tried or rejected. DELETED rows (admin-removed on the
 * review screen) surface under All only.
 */
const DETAILS_STATUS_GROUPS: Record<Exclude<DetailsFilterKey, 'all'>, BulkInviteRowStatus[]> = {
  invited: ['INVITED', 'ACCEPTED', 'REVOKED', 'EMAIL_NOT_SENT'],
  already: ['ALREADY_INVITED', 'ALREADY_REGISTERED'],
  failed: ['FAILED', 'INVALID'],
}

const DETAILS_FILTER_OPTIONS: { key: DetailsFilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'invited', label: 'Invited' },
  { key: 'already', label: 'Already invited' },
  { key: 'failed', label: 'Failed' },
]

const formatDate = (value: string | null) =>
  value ? moment(value).format('MM/DD/YYYY HH:mm') : '—'

interface BulkInviteHistoryProps {
  enabled: boolean
  /**
   * The tenant whose history to show — the active tenant a super admin is
   * viewing. Folded into the list query key so switching tenants gives each its
   * own cache entry instead of serving the previous tenant's rows until reload.
   */
  tenantId?: string
  /** Start correcting a request — routes the new file through the mapping flow. */
  onReupload?: (requestId: string) => void
}

/**
 * The tenant's bulk invitation history, rendered inside the modal.
 *
 * Tenant-wide, not per-caller: rows here may be another admin's upload, which is why
 * "Initiated by" is not decoration — it's how an admin knows whose file they're
 * looking at. Open to tenant admins and super admins alike.
 */
export default function BulkInviteHistory({
  enabled,
  tenantId,
  onReupload,
}: BulkInviteHistoryProps) {
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [statusFilter, setStatusFilter] = useState<BulkInviteStatus | 'ALL'>('ALL')
  const [selected, setSelected] = useState<BulkInviteRequest | null>(null)
  const [detailsPage, setDetailsPage] = useState(1)
  const [detailsStatusFilter, setDetailsStatusFilter] = useState<DetailsFilterKey>('all')

  const offset = useMemo(() => (page - 1) * rowsPerPage, [page, rowsPerPage])

  const filters = useMemo(
    () => ({
      limit: rowsPerPage,
      offset,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      // Cache-key only (not sent to the server) — scopes the list per tenant so a
      // super admin's tenant switch doesn't surface the previous tenant's rows.
      tenantId,
    }),
    [rowsPerPage, offset, statusFilter, tenantId],
  )

  const detailsOffset = useMemo(() => (detailsPage - 1) * DETAILS_PAGE_SIZE, [detailsPage])

  const detailsFilters = useMemo(
    () => ({
      limit: DETAILS_PAGE_SIZE,
      offset: detailsOffset,
      status:
        detailsStatusFilter === 'all' ? undefined : DETAILS_STATUS_GROUPS[detailsStatusFilter],
    }),
    [detailsOffset, detailsStatusFilter],
  )

  const { data, isLoading, isPlaceholderData } = useBulkInviteRequests(filters, { enabled })

  const {
    data: detailsData,
    isLoading: isDetailsLoading,
    isPlaceholderData: isDetailsPlaceholder,
  } = useBulkInviteRequestDetails(selected?.id, detailsFilters, { enabled: !!selected })

  const requests = data?.data || []
  const pageInfo = data?.pageInfo || { totalPages: 1, count: 0 }
  const totalPages = pageInfo.totalPages || 1

  const detailRows = detailsData?.data || []
  const detailsPageInfo = detailsData?.pageInfo || { totalPages: 1, count: 0 }
  const detailsTotalPages = detailsPageInfo.totalPages || 1

  // Show the loading state during filter/page transitions so stale rows (kept by
  // placeholderData) never contradict the active filter.
  const showSkeleton = isLoading || isPlaceholderData
  const isFiltered = statusFilter !== 'ALL'
  const showDetailsSkeleton = isDetailsLoading || isDetailsPlaceholder

  // Keep the current page in range when polling/refiltering shrinks the list.
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages)
    }
  }, [page, totalPages])

  // Reset details pagination and filter when a different row is selected.
  useEffect(() => {
    setDetailsPage(1)
    setDetailsStatusFilter('all')
  }, [selected?.id])

  const handleDetailsFilterChange = (next: DetailsFilterKey) => {
    setDetailsStatusFilter(next)
    setDetailsPage(1)
  }

  const handleStatusChange = (next: BulkInviteStatus | 'ALL') => {
    setStatusFilter(next)
    setPage(1)
  }

  // Inline details panel (avoids stacking a second modal on top of this one).
  if (selected) {
    return (
      <div className="flex flex-col gap-4 py-5 w-full">
        <div className="flex items-center gap-2">
          <Button
            isIconOnly
            radius="full"
            size="sm"
            variant="light"
            onPress={() => setSelected(null)}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <span className="font-medium break-all">{selected.fileName || 'Bulk invitation'}</span>
          <StatusChip status={selected.status} />

          {onReupload && selected.status !== 'SUCCEEDED' && (
            <Button
              className="ml-auto"
              // Re-uploading a running request would race the worker, so the
              // server only accepts it from a terminal status — mirror that here
              // rather than letting the admin discover it via a 400.
              isDisabled={isBulkInviteActive(selected.status)}
              radius="sm"
              size="sm"
              startContent={<Upload className="w-4 h-4" />}
              variant="bordered"
              onPress={() => onReupload(selected.id)}
            >
              Re-upload
            </Button>
          )}
        </div>

        <p className="text-xs text-gray-500 dark:text-[#AFAFAF]">
          Initiated by {formatInitiator(selected.initiator)}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <CountStat label="Invited" value={selected.successCount} />
          <CountStat label="Skipped" value={selected.skippedCount} />
          <CountStat label="Failed" value={selected.failureCount} />
          <CountStat label="Total" value={selected.totalRows} />
        </div>

        <EmailsNotSentNote count={selected.emailsNotSent} status={selected.status} />

        <StatusFilterChips
          active={detailsStatusFilter}
          ariaLabel="Filter rows by outcome"
          options={DETAILS_FILTER_OPTIONS}
          onChange={key => handleDetailsFilterChange(key as DetailsFilterKey)}
        />

        <RowDetailsTable
          isFiltered={detailsStatusFilter !== 'all'}
          isLoading={showDetailsSkeleton}
          rows={detailRows}
        />

        {!showDetailsSkeleton && detailsTotalPages > 1 && (
          <div className="flex justify-center gap-2">
            <Button
              isDisabled={detailsPage <= 1}
              radius="sm"
              size="sm"
              variant="flat"
              onPress={() => setDetailsPage(p => p - 1)}
            >
              Previous
            </Button>
            <span className="self-center text-sm text-gray-500 dark:text-[#AFAFAF]">
              {detailsPage} / {detailsTotalPages}
            </span>
            <Button
              isDisabled={detailsPage >= detailsTotalPages}
              radius="sm"
              size="sm"
              variant="flat"
              onPress={() => setDetailsPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    )
  }

  const renderCell = (item: any, columnKey: string) => {
    const request = item as BulkInviteRequest

    switch (columnKey) {
      case 'file':
        return (
          <span className="text-sm font-medium text-gray-900 dark:text-white break-all">
            {request.fileName || '—'}
          </span>
        )
      case 'initiator':
        return (
          <span className="text-sm text-gray-900 dark:text-white break-all">
            {formatInitiator(request.initiator)}
          </span>
        )
      case 'status':
        return <StatusChip status={request.status} />
      case 'progress':
        return (
          <span className="text-sm text-gray-900 dark:text-white">
            {getBulkInviteResolvedRows(request).toLocaleString()}/
            {request.totalRows.toLocaleString()} ({getBulkInviteProgress(request)}%)
          </span>
        )
      case 'created':
        return (
          <span className="text-gray-900 dark:text-white">{formatDate(request.createdAt)}</span>
        )
      case 'actions':
        return (
          <Button size="sm" variant="light" onPress={() => setSelected(request)}>
            Details
          </Button>
        )
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col gap-4 py-5 w-full">
      <StatusFilterChips
        active={statusFilter}
        ariaLabel="Filter requests by status"
        options={STATUS_OPTIONS}
        onChange={key => handleStatusChange(key as BulkInviteStatus | 'ALL')}
      />

      {!showSkeleton && requests.length === 0 ? (
        <EmptyState
          className="min-h-[240px]"
          icon={<EmptyStateIcon className="text-gray-400 dark:text-gray-500" />}
          title={isFiltered ? 'No matching requests' : 'No bulk invitations yet'}
          description={
            isFiltered
              ? 'No bulk invitations match this filter. Try a different status.'
              : 'Upload a CSV or Excel file from the "Upload" tab to invite many users at once. Your organization’s requests will appear here.'
          }
        />
      ) : (
        <TableComponent
          columns={columns}
          currentPage={page}
          customRenderCell={renderCell}
          data={requests}
          disableLocalPagination={true}
          initialVisibleColumns={['file', 'initiator', 'status', 'progress', 'created', 'actions']}
          isLoading={showSkeleton}
          rowsPerPage={rowsPerPage}
          selectionMode="none"
          showBottomContent={true}
          showTopContent={false}
          total={pageInfo.count || requests.length}
          totalPages={totalPages}
          trClassName="border-b dark:border-[#2A2A2A] border-[#ddd]"
          onPageChange={setPage}
          onLimitChange={(limit: number) => {
            setRowsPerPage(limit)
            setPage(1)
          }}
        />
      )}
    </div>
  )
}
